import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { radioNext, radioTabStop } from './segmentedChoice'

export interface SegmentedOption<T extends string> {
  value: T
  /** Text label. Omit for icon-only segments. */
  label?: ReactNode
  /** Material Symbols icon name (rendered alongside or in place of the label). */
  icon?: string
  /** Accessible title/tooltip — especially important for icon-only segments. */
  title?: string
  /** When true, the segment is dimmed and not selectable (e.g. work-in-progress). */
  disabled?: boolean
}

interface SegmentedProps<T extends string> {
  value: T
  onChange: (v: T) => void
  options: readonly SegmentedOption<T>[]
  /** `md` (default) — text/mixed labels. `lg` — Schulportal touch size (48 px, body text, DESIGN.md §17). `sm` — icon-only compact row. `xs` — compact text pills (e.g. filter rows above compact inputs). */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** Extra classes on the outer pill container. */
  className?: string
  /** Fill the parent width, segments share it evenly (e.g. above a same-width input). */
  stretch?: boolean
  /**
   * Text-only "slider" look (Schulportal, owner R42): equal-width segments, a solid primary indicator that SLIDES to the
   * chosen segment (transform only, `.sp-schieber` in index.css, off with prefers-reduced-motion and the Bewegung switch),
   * 48 px high (4 px padding + 40 px segments, no border) like the other header controls; a pill like the prototype's .seg;
   plain aria-pressed buttons in a group (Tab + Enter/Space, no tab roles without panels). Icons are ignored here.
   */
  slide?: boolean
  /**
   * Only with `slide`: the DENSE look for a row of FOUR labels that must fit a phone (Essen: Gestern / Heute / Morgen / Woche). Below 520 px the
   * segments take the width of their own label and the type steps down with the screen; from 520 px it is the look it always was.
   * Explicit on purpose: a slide control with four segments elsewhere (the Einstellungen language switcher) keeps its equal columns and
   * its 18 px type. Default (false): the slide look every existing user has.
   */
  dense?: boolean
  /**
   * Only with `slide` and without `dense`: the COMPACT look of a control inside a filter bar (Schulportal v44 `.fbar .seg.inline`: 44 px high segments,
   * 15 px type, 14 px side padding), so a two-segment day switch (Heute / Morgen) leaves room for the school choice next to it. Default (false): the slide look as always.
   */
  kompakt?: boolean
  /** Accessible name of the group (tab list, radio group or slide group). */
  ariaLabel?: string
  /**
   * The segments are the value of a form field (pick one of N, e.g. the school type), not tabs that switch a panel:
   * `radiogroup` + `radio` + `aria-checked` instead of `tablist` + `tab` + `aria-selected`, one Tab stop (the chosen
   * segment, else the first) and arrow keys that move AND choose. Default (false): the tab semantics every existing user has.
   * Ignored with `slide` (plain aria-pressed buttons).
   */
  choice?: boolean
}

// Owner 22.09.2026: sichtbarer Rand + kräftigere Auswahl, Tabs in Modals waren zu unauffällig
const OUTER_BASE = 'flex liquid-glass-pill p-1 rounded-xl border border-outline-variant'
const INNER_BASE = 'rounded-lg transition-all outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30'

/**
 * The slide look (see `slide` above). By default equal-width columns (unchanged for every caller). With `dense` (only the Essen page: Gestern /
 * Heute / Morgen / Woche): equal columns need four times the widest label, which does not fit a phone in French or English (Aujourd’hui,
 * Yesterday). So below 520 px the columns take the width of their own label and the type steps down with the screen (13 px at 320, 18 px
 * from 430, measured in de / en / fr / tr at 320 to 430 px: no label touches its neighbour or leaves the pill); from 520 px the columns are equal
 * again and the look is the one it always was. Content-width columns cannot be moved with a percentage, so the indicator is placed from the
 * measured button (offsetLeft / offsetWidth), re-measured when anything resizes (screen, language, font load).
 */
function SlideSegmented<T extends string>({ value, onChange, options, className, stretch, kompakt = false, ariaLabel, dicht }: {
  value: T; onChange: (v: T) => void; options: readonly SegmentedOption<T>[]; className: string; stretch: boolean; ariaLabel?: string; dicht: boolean; kompakt?: boolean
}) {
  const n = Math.max(1, options.length)
  const aktiv = Math.max(0, options.findIndex((o) => o.value === value))
  const pille = useRef<HTMLDivElement>(null)
  const [geo, setGeo] = useState<{ x: number; w: number } | null>(null)
  // The first placement is a jump; the glide starts with the next change.
  const [bereit, setBereit] = useState(false)
  useLayoutEffect(() => {
    const el = pille.current
    if (!dicht || !el || typeof ResizeObserver === 'undefined') return
    const messen = () => {
      const b = el.querySelectorAll<HTMLButtonElement>('button')[aktiv]
      // offsetLeft / offsetWidth are whole pixels (a transformed parent cannot skew them): half a pixel of bleed on each side keeps a fractional button covered.
      if (b) setGeo((g) => (g && g.x === b.offsetLeft - 0.5 && g.w === b.offsetWidth + 1 ? g : { x: b.offsetLeft - 0.5, w: b.offsetWidth + 1 }))
    }
    messen()
    const ro = new ResizeObserver(messen)
    ro.observe(el)
    el.querySelectorAll('button').forEach((b) => ro.observe(b))
    return () => ro.disconnect()
  }, [dicht, aktiv, n])
  const gemessen = geo !== null
  useEffect(() => {
    if (!gemessen) return
    const id = requestAnimationFrame(() => setBereit(true))
    return () => cancelAnimationFrame(id)
  }, [gemessen])
  return (
    <div ref={pille} role="group" aria-label={ariaLabel} className={`relative inline-grid grid-flow-col ${dicht ? 'auto-cols-auto min-[520px]:auto-cols-fr' : 'auto-cols-fr'} rounded-full liquid-glass-pill p-1 ${stretch ? 'w-full' : ''} ${className}`}>
      <span
        aria-hidden="true"
        className="sp-schieber pointer-events-none absolute inset-y-1 rounded-full bg-primary shadow-sm"
        style={geo && dicht
          ? { left: 0, width: geo.w, transform: `translateX(${geo.x}px)`, ...(bereit ? {} : { transition: 'none' }) }
          : { left: '0.25rem', width: `calc((100% - 0.5rem) / ${n})`, transform: `translateX(${aktiv * 100}%)` }}
      />
      {options.map((opt) => {
        const selected = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={selected}
            disabled={opt.disabled}
            title={opt.title}
            onClick={() => { if (!opt.disabled) onChange(opt.value) }}
            className={`relative z-10 flex min-h-10 items-center justify-center whitespace-nowrap rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 ${
              dicht
                ? 'min-w-11 px-2 text-[13px] min-[340px]:text-sm min-[360px]:text-[15px] min-[375px]:text-base min-[430px]:px-3 min-[430px]:text-lg min-[520px]:px-5'
                : kompakt ? '!min-h-11 px-3.5 text-[15px] max-[520px]:px-1.5' : 'px-5 text-lg'
            } ${selected ? 'font-bold text-on-primary' : 'font-semibold text-on-surface hover:text-primary'}`}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Canonical segmented control. DESIGN.md §14 — glass pill outer, white
 * "press-in" on the selected segment. Replaces the per-page pattern of
 * `<div className="flex liquid-glass-pill p-1 rounded-xl"> <button...>`.
 *
 * Generic on the option-value union so TypeScript infers the selected
 * value + onChange signature:
 *
 *   <Segmented<'grid' | 'list'>
 *     value={view} onChange={setView}
 *     options={[
 *       { value: 'grid', icon: 'grid_view', title: 'Kacheln' },
 *       { value: 'list', icon: 'list',      title: 'Liste'   },
 *     ]}
 *   />
 */
export function Segmented<T extends string>({
  value, onChange, options, size = 'md', className = '', stretch = false, slide = false, dense = false, kompakt = false, ariaLabel, choice = false,
}: SegmentedProps<T>) {
  if (slide) return <SlideSegmented value={value} onChange={onChange} options={options} className={className} stretch={stretch} kompakt={kompakt} ariaLabel={ariaLabel} dicht={dense} />
  const inner = `${size === 'lg' ? 'px-5 min-h-12 text-lg' : size === 'sm' ? 'p-2' : size === 'xs' ? 'px-3 py-2.5 text-xs font-bold lg:px-2 lg:py-1 lg:text-[11px]' : 'px-3.5 py-2 text-[13px]'}${stretch ? ' flex-1 text-center' : ''}`
  // Radio group: Tab stops on the chosen segment only (the first enabled one while nothing is chosen); arrows move and choose.
  const tabStop = radioTabStop(options, value)
  const onRadioKey = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next = radioNext(options, index, e.key)
    if (next === null) return
    e.preventDefault()
    e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus()
    onChange(options[next].value)
  }
  // Owner 22.09.2026: md-Tabs jetzt mit eigenem Schriftgewicht je Zustand (xs behält sein festes font-bold)
  return (
    <div className={`${OUTER_BASE} ${stretch ? 'w-full' : ''} ${className}`} role={choice ? 'radiogroup' : 'tablist'} aria-label={ariaLabel}>
      {options.map((opt, index) => {
        const selected = opt.value === value
        const disabled = !!opt.disabled
        const weight = size === 'md' || size === 'lg' ? (selected ? 'font-bold' : 'font-semibold') : ''
        // `border` on every state: a border that only the selected pill carries
        // would move its neighbours by 1px on each click.
        const cls = `${INNER_BASE} ${inner} ${weight} border ${
          disabled
            ? 'border-transparent text-on-surface-variant/40 cursor-not-allowed'
            : selected
              ? 'bg-surface-container-lowest shadow border-primary/30 text-primary'
              : 'border-transparent text-on-surface hover:text-primary'
        }`
        return (
          <button
            key={opt.value}
            type="button"
            {...(choice
              ? { role: 'radio', 'aria-checked': selected, tabIndex: index === tabStop ? 0 : -1, onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => onRadioKey(e, index) }
              : { role: 'tab', 'aria-selected': selected })}
            aria-disabled={disabled}
            disabled={disabled}
            title={opt.title}
            onClick={() => { if (!disabled) onChange(opt.value) }}
            className={cls}
          >
            {opt.icon && (
              <span className={`material-symbols-outlined ${size === 'sm' ? '!text-xl' : size === 'xs' ? '!text-[14px]' : '!text-[16px]'}`}>
                {opt.icon}
              </span>
            )}
            {opt.label && <span className={opt.icon ? 'ml-1.5' : ''}>{opt.label}</span>}
          </button>
        )
      })}
    </div>
  )
}
